import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { ErrorResponse } from "@reflag/shared/api";
import {
  FeatureTargetingHistoryQueryArgs,
  FeatureTargetingVersion,
} from "@reflag/shared/featureTargetingAPI";
import { Paginated } from "@reflag/shared/types/Paginated";

export function useFeatureTargetingHistoryData(
  envId: string,
  featureId: string,
  params?: Omit<FeatureTargetingHistoryQueryArgs, "envId">,
  options?: Omit<
    UseInfiniteQueryOptions<
      Paginated<FeatureTargetingVersion, "version">,
      AxiosError<ErrorResponse>,
      FeatureTargetingVersion[],
      QueryKey,
      number
    >,
    "queryKey" | "queryFn"
  >,
): UseInfiniteQueryResult<
  FeatureTargetingVersion[],
  AxiosError<ErrorResponse>
> {
  const { app } = useEnvironment();
  const baseParams = {
    pageSize: 100,
    ...params,
  };

  return useInfiniteQuery({
    queryKey: featureQueryKeys.targetingVersions(
      app.id,
      envId,
      featureId,
      baseParams,
    ),
    queryFn: ({ pageParam = 0 }) =>
      api
        .get<"/apps/:appId/features/:featureId/targeting/history">(
          `/apps/${app.id}/features/${featureId}/targeting/history`,
          {
            params: {
              pageIndex: pageParam,
              ...baseParams,
              envId: envId!,
            },
          },
        )
        .then((res) => res.data),
    initialPageParam: params?.pageIndex ?? 0,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.pageIndex + 1;
      const totalPages = Math.ceil(lastPage.totalCount / lastPage.pageSize);
      return nextPage < totalPages ? nextPage : undefined;
    },
    select: ({ pages }) => pages.flatMap((page) => page.data),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}
